/**
 * FFFormFields — Formulaire principal d'édition d'une facture fournisseur.
 *
 * Organisation visuelle améliorée :
 *   - Section 1 (toujours visible) : Identité (fournisseur, n° facture, dates)
 *   - Section 2 (toujours visible) : Montants (HT, TVA, TTC) — la plus utilisée
 *   - Section 3 (collapse, déjà ouverte) : Comptabilité (compte charge, TVA)
 *   - Section 4 (collapse, fermée par défaut) : Références (QR, RF, IBAN)
 *   - Section 5 (collapse, fermée par défaut) : Description + Notes internes
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronDown, ChevronRight, Save, Loader2, Sparkles } from "lucide-react";
import CompteSelect from "@/components/CompteSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 11 comptes les plus utilisés pour les achats fournisseur (mis en avant en groupe "recommandés").
const COMPTES_RECOMMANDES = [
  { value: "4200", label: "4200 · Achats marchandises (pièces, huile, pneus…)" },
  { value: "4400", label: "4400 · Achats services / sous-traitance" },
  { value: "6000", label: "6000 · Loyer locaux" },
  { value: "6100", label: "6100 · Énergie / électricité" },
  { value: "6200", label: "6200 · Entretien / réparations" },
  { value: "6300", label: "6300 · Assurances" },
  { value: "6400", label: "6400 · Frais véhicules / carburant" },
  { value: "6500", label: "6500 · Frais administratifs / télécom" },
  { value: "6600", label: "6600 · Publicité / marketing" },
  { value: "6800", label: "6800 · Amortissements" },
  { value: "1500", label: "1500 · Mobilier / machines de bureau (immobilisations)" },
];
const RECOMMANDES_SET = new Set(COMPTES_RECOMMANDES.map((c) => c.value));

const TVA_TAUX = [
  { value: "8.1", label: "8.1 % (normal CH 2025)" },
  { value: "2.6", label: "2.6 % (réduit alimentation)" },
  { value: "3.8", label: "3.8 % (hébergement)" },
  { value: "0", label: "0 % (exonéré)" },
];

function Section({ title, icon, children, open, onToggle, badge }) {
  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 hover:bg-gray-50 transition px-1"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          {icon}
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {badge}
        </div>
      </button>
      {open && <div className="pb-3 space-y-3">{children}</div>}
    </div>
  );
}

export default function FFFormFields({ f, editable, compteVerrouille, saving, onSave, updateField, updateMontant, updateTauxTva }) {
  const [openSections, setOpenSections] = useState({
    identite: true,
    montants: true,
    compta: true,
    refs: false,
    description: false,
  });
  const toggle = (k) => setOpenSections((p) => ({ ...p, [k]: !p[k] }));

  // Charge tous les comptes du plan comptable (cas factures complexes)
  const [allComptes, setAllComptes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/journal/plan-comptes`).then((r) => {
      if (!cancelled) setAllComptes(Array.isArray(r.data) ? r.data : []);
    }).catch(() => { /* silencieux, on retombe sur les recommandés seuls */ });
    return () => { cancelled = true; };
  }, []);

  // Groupes du sélecteur de compte : ⭐ Recommandés + Tous les comptes (le reste).
  const compteGroups = [
    { title: "⭐ Comptes recommandés", options: COMPTES_RECOMMANDES },
    {
      title: "Tous les comptes (plan comptable)",
      options: allComptes
        .filter((c) => c.actif !== false && !RECOMMANDES_SET.has(c.numero))
        .sort((a, b) => (a.numero || "").localeCompare(b.numero || ""))
        .map((c) => ({ value: c.numero, label: `${c.numero} · ${c.libelle}` })),
    },
  ];

  const ocr = f.donnees_ocr;
  const ibanValue = f.fournisseur_iban_edit !== undefined
    ? f.fournisseur_iban_edit
    : (f.donnees_ocr?.fournisseur_iban || "");

  return (
    <Card className="p-4" data-testid="ff-form-card">
      <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
        📋 Données de la facture
      </h3>

      {/* === Identité === */}
      <Section
        title="Identité"
        icon={<span className="text-base">🏢</span>}
        open={openSections.identite}
        onToggle={() => toggle("identite")}
      >
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Fournisseur *</Label>
          <Input
            value={f.fournisseur_nom || ""}
            onChange={(e) => updateField("fournisseur_nom", e.target.value)}
            disabled={!editable}
            placeholder="Nom du fournisseur"
            data-testid="ff-input-fournisseur"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">N° facture *</Label>
            <Input
              value={f.numero_facture || ""}
              onChange={(e) => updateField("numero_facture", e.target.value)}
              disabled={!editable}
              placeholder="ex: 2026-0478"
              data-testid="ff-input-numero"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Devise</Label>
            <Input
              value={f.devise || "CHF"}
              onChange={(e) => updateField("devise", e.target.value)}
              disabled={!editable}
              className="font-mono"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Date facture *</Label>
            <Input
              type="date" value={f.date_facture || ""}
              onChange={(e) => updateField("date_facture", e.target.value)}
              disabled={!editable}
              data-testid="ff-input-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Échéance</Label>
            <Input
              type="date" value={f.date_echeance || ""}
              onChange={(e) => updateField("date_echeance", e.target.value)}
              disabled={!editable}
            />
          </div>
        </div>
      </Section>

      {/* === Montants === */}
      <Section
        title="Montants"
        icon={<span className="text-base">💰</span>}
        open={openSections.montants}
        onToggle={() => toggle("montants")}
      >
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">HT</Label>
            <Input
              type="number" step="0.01" value={f.montant_ht ?? ""}
              onChange={(e) => updateMontant("montant_ht", e.target.value)}
              disabled={!editable} className="font-mono text-right"
              data-testid="ff-input-ht"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">TVA</Label>
            <Input
              type="number" step="0.01" value={f.tva_montant ?? ""}
              onChange={(e) => updateMontant("tva_montant", e.target.value)}
              disabled={!editable} className="font-mono text-right"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">TTC *</Label>
            <Input
              type="number" step="0.01" value={f.montant_ttc ?? ""}
              onChange={(e) => updateMontant("montant_ttc", e.target.value)}
              disabled={!editable} className="font-mono text-right font-bold"
              data-testid="ff-input-ttc"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Taux TVA</Label>
          <Select
            value={f.tva_taux != null ? String(f.tva_taux) : ""}
            onValueChange={(v) => updateTauxTva(v)}
            disabled={!editable}
          >
            <SelectTrigger data-testid="ff-select-tva"><SelectValue placeholder="Choisir le taux" /></SelectTrigger>
            <SelectContent>
              {TVA_TAUX.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Section>

      {/* === Comptabilité === */}
      <Section
        title="Comptabilité"
        icon={<span className="text-base">📚</span>}
        open={openSections.compta}
        onToggle={() => toggle("compta")}
        badge={
          ocr?.compte_charge_suggere && !f.compte_charge ? (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px] h-4 px-1.5">
              <Sparkles className="w-2.5 h-2.5 mr-0.5" /> IA suggère {ocr.compte_charge_suggere}
            </Badge>
          ) : null
        }
      >
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Compte de charge *</Label>
          <CompteSelect
            value={f.compte_charge || ""}
            onValueChange={(v) => updateField("compte_charge", v)}
            disabled={compteVerrouille}
            groups={compteGroups}
            placeholder="Choisir un compte"
            data-testid="ff-select-compte"
          />
        </div>
      </Section>

      {/* === Références (QR / RF / IBAN) === */}
      <Section
        title="Références bancaires"
        icon={<span className="text-base">🏦</span>}
        open={openSections.refs}
        onToggle={() => toggle("refs")}
        badge={
          (f.qr_reference || f.rf_reference) ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] h-4 px-1.5">
              ✓ {f.qr_reference ? "QR-bill" : "RF"}
            </Badge>
          ) : null
        }
      >
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            IBAN fournisseur
            {ocr?.fournisseur_iban && !(f.fournisseur_iban_edit) && (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[9px] h-4 px-1">
                <Sparkles className="w-2.5 h-2.5 mr-0.5" /> IA détecté
              </Badge>
            )}
          </Label>
          <Input
            value={ibanValue}
            onChange={(e) => updateField("fournisseur_iban_edit", e.target.value.toUpperCase())}
            disabled={!editable}
            placeholder="CH93 0076 2011 6238 5295 7"
            className="font-mono text-xs"
            data-testid="ff-input-iban"
          />
          <p className="text-[10px] text-gray-400 mt-0.5">Format ISO 13616 (CH, LI, FR, DE, IT…) · validation au save</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Réf. QR-bill</Label>
            <Input
              value={f.qr_reference || ""}
              onChange={(e) => updateField("qr_reference", e.target.value)}
              disabled={!editable} placeholder="27 chiffres" className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Réf. RF</Label>
            <Input
              value={f.rf_reference || ""}
              onChange={(e) => updateField("rf_reference", e.target.value)}
              disabled={!editable} placeholder="RFxx…" className="font-mono text-xs"
            />
          </div>
        </div>
      </Section>

      {/* === Description / Notes === */}
      <Section
        title="Description & notes internes"
        icon={<span className="text-base">📝</span>}
        open={openSections.description}
        onToggle={() => toggle("description")}
        badge={(f.description || f.notes) ? <Badge className="bg-gray-100 text-gray-600 text-[9px] h-4 px-1.5">✓ rempli</Badge> : null}
      >
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
          <Textarea
            value={f.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
            disabled={!editable} rows={2}
            placeholder="Bref descriptif du contenu de la facture"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Notes internes</Label>
          <Textarea
            value={f.notes || ""}
            onChange={(e) => updateField("notes", e.target.value)}
            rows={2}
            placeholder="Commentaire interne (visible uniquement par l'équipe)"
          />
        </div>
      </Section>

      {editable && (
        <Button
          onClick={onSave}
          disabled={saving}
          className="w-full bg-gray-900 text-white hover:bg-gray-700 mt-4"
          data-testid="ff-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer les modifications
        </Button>
      )}
    </Card>
  );
}
